import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';

import { ProviderService } from '../services/provider.service';
import { providerBasePath, routeTypeFor } from './provider-routing';

/**
 * Keeps a provider inside the route tree for its type. `expected` is the tree
 * this guard sits on (a typed tree's type, or null for the generic tree). A
 * provider opened in the wrong tree — an old /admin/providers/:id link, or a
 * hand-edited type segment — is redirected to the same page (everything after
 * the provider id, plus query params) in the right tree.
 *
 * The type comes from ProviderService's cache when the provider list or a
 * detail page already loaded it, so normal navigation doesn't fetch twice.
 * A failed lookup lets the navigation through; the page reports the error.
 */
export function providerTypeGuard(expected: string | null): CanActivateFn {
  return (route, state) => {
    const providerService = inject(ProviderService);
    const router = inject(Router);
    const providerId = route.paramMap.get('providerId') ?? '';

    return providerService.getType(providerId).pipe(
      map((type) => {
        const routeType = routeTypeFor(type);
        if (routeType === expected) return true;
        const path = router.parseUrl(state.url).root.children['primary']?.segments.map((s) => s.path) ?? [];
        const tail = path.slice(path.indexOf(providerId) + 1);
        return router.createUrlTree([...providerBasePath(routeType, providerId), ...tail], {
          queryParams: route.queryParams,
        });
      }),
      catchError(() => of(true)),
    );
  };
}
